import { useEffect, useState } from 'react'
import {
  CubeIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  CurrencyEuroIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner, EmptyState, Button } from '@/components/ui'
import { servicesApiCatalog } from '@/api/services'
import type { ServiceCategory, Service } from '@/api/services'

type PriceFilter = 'all' | 'free' | 'paid'

export default function ServicesPage() {
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchCategories()
    fetchServices()
  }, [])

  useEffect(() => {
    fetchServices()
  }, [selectedCategory, searchQuery])

  const fetchCategories = async () => {
    try {
      const data = await servicesApiCatalog.getServiceCategories()
      setCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
      setCategories([])
    }
  }

  const fetchServices = async () => {
    setLoading(true)
    try {
      const data = await servicesApiCatalog.getServices({
        category_slug: selectedCategory || undefined,
        search: searchQuery || undefined,
      })
      setServices(data || [])
    } catch (error) {
      console.error('Error fetching services:', error)
      setServices([])
    } finally {
      setLoading(false)
    }
  }

  // Filter services by price
  const filteredServices = services.filter(service => {
    if (priceFilter === 'free') return service.is_free
    if (priceFilter === 'paid') return !service.is_free
    return true
  })

  // Group services by category
  const groupedServices = filteredServices.reduce((acc, service) => {
    const categoryName = service.category_name || 'Altri'
    if (!acc[categoryName]) {
      acc[categoryName] = []
    }
    acc[categoryName].push(service)
    return acc
  }, {} as Record<string, Service[]>)

  const handleRequestService = (service: Service) => {
    // Placeholder - funzionalita' in arrivo
    alert(`La funzionalita' di richiesta per il servizio "${service.name}" sara' disponibile a breve.`)
  }

  if (loading && !services.length) {
    return <InlineSpinner message="Caricamento servizi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Servizi Compliance Clinic</h1>
        <p className="text-gray-600">
          Scopri e richiedi i servizi disponibili per la tua attivita di compliance
        </p>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca servizi..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap items-center gap-4">
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('')}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !selectedCategory
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Tutte le categorie
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedCategory === cat.slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            {/* Price Filter */}
            <div className="flex items-center gap-2 border-l pl-4 border-gray-200">
              <span className="text-sm text-gray-500">Tipo:</span>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setPriceFilter('all')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    priceFilter === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Tutti
                </button>
                <button
                  type="button"
                  onClick={() => setPriceFilter('free')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    priceFilter === 'free'
                      ? 'bg-green-100 text-green-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CheckBadgeIcon className="h-3.5 w-3.5 inline mr-1" />
                  Gratuiti
                </button>
                <button
                  type="button"
                  onClick={() => setPriceFilter('paid')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    priceFilter === 'paid'
                      ? 'bg-amber-100 text-amber-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CurrencyEuroIcon className="h-3.5 w-3.5 inline mr-1" />
                  A Pagamento
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Services Grid */}
      {filteredServices.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(groupedServices).map(([categoryName, categoryServices]) => (
            <div key={categoryName}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{categoryName}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryServices.map((service) => (
                  <Card key={service.id} className="hover:shadow-md transition-shadow flex flex-col">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <CubeIcon className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{service.name}</h3>
                            {service.provider && (
                              <p className="text-xs text-gray-500">{service.provider}</p>
                            )}
                          </div>
                        </div>
                        <Badge
                          variant={service.is_free ? 'success' : 'warning'}
                          size="sm"
                        >
                          {service.is_free ? (
                            <>
                              <CheckBadgeIcon className="h-3 w-3 mr-1" />
                              Gratuito
                            </>
                          ) : (
                            <>
                              <CurrencyEuroIcon className="h-3 w-3 mr-1" />
                              A pagamento
                            </>
                          )}
                        </Badge>
                      </div>

                      <p className="text-sm text-gray-600">
                        {service.short_description}
                      </p>

                      {service.description && (
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {service.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <Button
                        size="sm"
                        onClick={() => handleRequestService(service)}
                        className="flex items-center gap-1"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" />
                        Richiedi Servizio
                      </Button>

                      {service.external_url && (
                        <a
                          href={service.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Info
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<CubeIcon className="h-12 w-12" />}
            title="Nessun servizio trovato"
            description={searchQuery || selectedCategory || priceFilter !== 'all'
              ? "Prova a modificare i filtri di ricerca"
              : "I servizi saranno disponibili a breve"
            }
          />
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center shrink-0">
            <CubeIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Come funziona</h3>
            <p className="text-sm text-gray-600">
              I servizi gratuiti sono strumenti a disposizione di tutti i membri.
              I servizi a pagamento permettono di richiedere assistenza specializzata per la tua attivita.
              Clicca su "Richiedi Servizio" per inviare la tua richiesta.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
